import React from 'react';

import cx from 'classnames';
import shortid from 'shortid';
import { useDispatch, useSelector } from "react-redux";
import _ from 'lodash';

import S3Upload from 'react-s3-uploader/s3upload'
import { toast } from "react-toastify";
import { FailureToast, SuccessToast } from "components/Toast";
import { MIXPANEL_DISTRIBUTION_CLICKED } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";
import { getStoryboardMixpanelEventPayload } from "services/mixpanelUtil";

import AuthService from 'services/auth.service';

import styles from './buttons.module.scss';

const API_URL = `${process.env.REACT_APP_PAPERFLITE_API_URL}/api/2.0`;
const applicationUrl = `${process.env.REACT_APP_UI_URL}`;

export function PrimaryButton({
  type,
  text,
  icon,
  onClick,
  className,
  disabled = false,
  ...rest
}) {
  return (
    <button
      className={cx(styles.primaryButton, className)}
      type={type}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      {text}
    </button>
  );
}

export function SecondaryButton({
  type,
  text,
  onClick,
  className,
  disabled = false,
  ...rest
}) {
  return (
    <button
      className={cx(styles.secondaryButton, className)}
      type={type}
      onClick={onClick}
      disabled={disabled}
    >
      {text}
    </button>
  );
}

export function FileUploadButton({className, name, accept, multiple, children, onStart, onProgress, onComplete, onError, limit = 15, handleActiveMedia}) {

  const handleUploadProgress = (progress, textState, file) => {
    if(onProgress){
      onProgress(file, progress);
    }
  }

  const handleFileCheck = (file,next)=>{

    const fileSizeExceeded = (file.size / 1048576) > limit

    if (file.type === 'video/mp4' && fileSizeExceeded) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`The video size should be less than 15 MB`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
      
    } else {
      next(file);
    }
  }

  const removeSpecialCharactersFromName = (name)=>{
    return name.replace(/\u202F/g, '');
  }

  const handleFileChange = (e) => {
    const uploaders = Array.from(e.target.files).flatMap(file => {
      const acceptFiles = (accept ? (Array.isArray(accept) ? accept : [accept]) : []).map((data) => {
        return data;
      })
      const mergedArray = acceptFiles.flat();
      
      let fileType = file.type
      if (file.name.endsWith('.ttf') && !fileType) {
        fileType = 'font/ttf';
      }

      if((mergedArray?.length > 0 && (!fileType || !mergedArray.includes(fileType)))) {
        toast(
          <FailureToast
            heading={`Failure`}
            description={`File type ${file.type} is not supported`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
        return [];
      };

      if(e.target.files?.length === 1 && handleActiveMedia) {
        let active = 'image';
        if(fileType === 'video/mp4') {
          active = 'video';
        } else {
          active = 'image';
        }

        handleActiveMedia(active);
      }

      file.id = shortid();
      var updatedName = removeSpecialCharactersFromName(file.name)
      var s3Uploader = new S3Upload({
        signingUrl: "/file_upload/initiate",
        signingUrlMethod: "POST",
        signingUrlHeaders: {
          Authorization: `Bearer ${AuthService.getAccessToken()}`
        },
        server: API_URL,
        scrubFilename: (filename) => {
          var name = removeSpecialCharactersFromName(filename);
          name = name.replace(/[^\w\d_\-.\s]+/ig, '')
          return name;
        },
        signingUrlQueryParams: {'originalName': encodeURIComponent(updatedName)},
        contentDisposition: 'auto',
        preprocess:handleFileCheck,
        uploadRequestHeaders: {},
        onFinishS3Put: onComplete,
        onProgress: handleUploadProgress,
        onError: onError, 
        files: [file]
      });

      return s3Uploader;
    });

    if(onStart && uploaders.length > 0){
      onStart(uploaders);
    }
  }

  return (
    <div className={cx(styles.fileUpload, className)}>
      {children}
      <input 
        type="file" 
        title=''
        name={name} 
        accept={accept} 
        multiple={multiple} 
        onClick={(e) => e.stopPropagation()} 
        onChange={handleFileChange}
      />
    </div>
  );
}

export function CopyEmbedCodeButton({className, id, domain, height, children, socialShare = false}) {
  const textareaRef = React.createRef(null);
  const dispatch = useDispatch();
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  const getCode = () => {
    return (
      `<div class="cleverstory__live_element" data-live-element="${domain}/embedded/${id}" data-id="${id}" data-width="100%" data-height="${height}px">
        <div id="page-${id}" style="display: none;"></div>
       </div>
      <script>
      !function(){var t=window,e=t.Cleverstory;if("function"==typeof e)e();else{var a=document,n=function(){var t=a.createElement("script");t.type="text/javascript",t.async=!0,t.src="${applicationUrl}/scripts/embed_page.min.js";var e=a.getElementsByTagName("script")[0];e.parentNode.insertBefore(t,e)};t.attachEvent?t.attachEvent("onload",n):t.addEventListener("load",n,!1)}}();
      </script>
      `
    );
  }

  const handleCopy = () => {    
    textareaRef.current.select();
    document.execCommand('copy');
    toast(
      <SuccessToast
        heading={`Success`}
        description={`Copied embed code to clipboard!`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );

    const mixpanelPayload = getStoryboardMixpanelEventPayload(storyboard.id, storyboard.name, storyboard.type);
    mixpanelPayload.social_platform = "Embed";
    dispatch(createMixpanelEvent(MIXPANEL_DISTRIBUTION_CLICKED, mixpanelPayload));
  }

  return (
    <div className={cx(styles.copyEmbedCodeButton, className)} onClick={handleCopy}>
      {children}
      <textarea
        ref={textareaRef}
        className={styles.embedCodeTextArea}
        readOnly={true}
        value={getCode()} />
    </div>
  )
}